import { booleanAttribute } from "#ce/utils/props";

// `value` is absent on purpose: the element owns a live `value` property, which a declared prop
// would shadow with an accessor onto the prop store. The attribute seeds it and names the reset value.
export const props = Object.freeze({
  /**
   * The selectable values. When set, the slider snaps through this list (and
   * typed values snap to the nearest entry) instead of stepping through the
   * min/max/step range. Accepts an array or, when set as an attribute, a JSON
   * array string (e.g. values="[5,10,25,50]").
   *
   * @type {Array|String}
   */
  values: {
    type: [Array, String],
    default: undefined,
  },
  /**
   * Smallest selectable value. Ignored when values is set.
   *
   * @type {Number|String}
   * @default 1
   * @storybook-control number
   */
  min: {
    type: [Number, String],
    default: 1,
  },
  /**
   * Largest selectable value. Ignored when values is set.
   *
   * @type {Number|String}
   * @default 150
   * @storybook-control number
   */
  max: {
    type: [Number, String],
    default: 150,
  },
  /**
   * Step increment between selectable values. Ignored when values is set.
   *
   * @type {Number|String}
   * @default 1
   * @storybook-control number
   */
  step: {
    type: [Number, String],
    default: 1,
  },
  /**
   * Label shown above the numeric input; omitted from the DOM when unset.
   * Doubles as the numeric input's accessible name, so always provide one —
   * without it the input is unnamed for assistive technology. (The slider's
   * own focusable control is not labelled either way — sp-input-range-slider
   * does not yet forward aria attributes to it.)
   *
   * @type {String}
   */
  label: {
    type: String,
    default: undefined,
  },
  /**
   * Stable data-testid applied to the numeric input for QA journeys.
   *
   * @type {String}
   */
  inputTestId: {
    type: String,
    default: undefined,
  },
  /**
   * Disable both the numeric input and the slider so they ignore input.
   *
   * @type {Boolean|String}
   * @default false
   */
  disabled: booleanAttribute(),
});
